import React, { useState, useRef, useEffect } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Flex, message } from "antd";
import { CustomModal, CustomButton, CustomParagraph } from "../../components";
import { uploadFile } from "../../api's/shipment";
import { useSpin } from "../../components/SpinContext";

const FileUploadModal = ({
  modalOpen,
  setModalOpen,
  shipmentId,
  token,
  fetchSingleShipmentData,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const { showSpin, hideSpin } = useSpin();

  const handleFileSelect = (event) => {
    event.stopPropagation();
    if (event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
    event.target.value = "";
  };

  const openFileDialog = (event) => {
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  const removeFile = (event) => {
    event.stopPropagation();
    setSelectedFile(null);
  };

  const unauthorize = (error) => {
    if (error?.status === 401) {
      setModalOpen(false);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    if (!shipmentId) {
      message.error("Shipment ID is missing!");
      return;
    }

    const formData = new FormData();
    formData.append("shipment_id", shipmentId);
    formData.append("file", selectedFile);

    showSpin();
    uploadFile(token, formData)
      .then((res) => {
        const data = res?.data || null;
        if (data?.is_success) {
          message.success(data?.message);
          fetchSingleShipmentData();
          setModalOpen(false);
          setSelectedFile(null);
        } else {
          message.error(data?.message);
        }
      })
      .catch((error) => {
        unauthorize(error);
        message.error("Upload failed. Please try again.");
      })
      .finally(() => {
        hideSpin();
      });

    console.log("File ready to be uploaded:", selectedFile);
  };

  useEffect(() => {
    if (!modalOpen) {
      setSelectedFile(null);
    }
  }, [modalOpen]);

  return (
    <CustomModal
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      modalTitle="Upload file"
      modalSize={500}
      buttonText1={selectedFile ? "Cancel" : null}
      buttonText2={selectedFile ? "Upload" : null}
      isFormValid={!selectedFile}
      onConfirmClick={handleUpload}
    >
      <div
        style={{
          border: selectedFile ? "none" : "2px dashed #EEEEEE",
          borderRadius: "8px",
          padding: selectedFile ? "0px" : "20px",
          textAlign: "center",
          cursor: "pointer",
        }}
        onClick={openFileDialog}
        onDragOver={(event) => event.preventDefault()} 
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.dataTransfer.files.length > 0) {
            setSelectedFile(event.dataTransfer.files[0]); 
          }
        }}
      >
        {!selectedFile ? (
          <>
            <CustomParagraph
              text="Drop file here to upload..."
              fontSize="12px"
              fontWeight="400"
              color="#4B4B4B"
              textAlign="center"
              marginBottom="10px"
            />
            <CustomButton
              text="Browse files"
              width="120px"
              height="36px"
              fontSize="12px"
              fontWeight="600"
              borderRadius="20px"
              bgColor="#F3F3F3"
              color="#000"
              boxShadow="none"
              onClick={openFileDialog}
              iconPosition="left"
              gap={8}
            />
          </>
        ) : (
          <Flex
            onClick={(event) => event.stopPropagation()}
            align="center"
            justify="space-between"
            style={{
              backgroundColor: "#F5F5F5",
              padding: "10px",
              borderRadius: "8px",
              marginTop: "10px",
            }}
          >
            <div>
              <CustomParagraph
                text={
                  selectedFile.name.length > 40
                    ? `${selectedFile.name.substring(0, 40)}...`
                    : selectedFile.name
                }
                fontSize="14px"
                fontWeight="600"
                color="#000"
                textAlign="left"
                marginBottom="4px"
              />
              <CustomParagraph
                text={`${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`}
                fontSize="12px"
                fontWeight="400"
                color="#4B4B4B"
                textAlign="left"
              />
            </div>
            <span title="Close" className="no-bg" onClick={removeFile}>
              <CloseOutlined color="#000" />
            </span>
          </Flex>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
    </CustomModal>
  );
};

export default FileUploadModal;
